import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useRedeemSignupCode } from '../api/hooks.js';
import { setWorkspaceId } from '../workspace.js';
import TurnstileGate, { turnstileEnabled } from '../components/TurnstileGate.js';
import { ApiError } from '../api/client.js';
import { formatUnexpectedError } from '../lib/errors.js';

export default function Signup() {
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get('code') ?? '').toUpperCase());
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const redeem = useRedeemSignupCode();
  const navigate = useNavigate();

  const needsToken = turnstileEnabled();
  const blocked = needsToken && !turnstileToken;

  return (
    <div className="mx-auto mt-16 max-w-md px-6">
      <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
        ← Back
      </Link>
      <div className="card mt-4 p-6">
        <h1 className="text-xl font-semibold text-slate-900">Create your account</h1>
        <p className="mt-1 text-sm text-slate-600">
          Got an invite code from someone on adhc.to? Redeem it here.
        </p>
        <form
          className="mt-5 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            try {
              const res = await redeem.mutateAsync({
                code: code.trim(),
                email: email.trim(),
                name: name.trim(),
                workspaceName: workspaceName.trim(),
                turnstileToken,
              });
              setWorkspaceId(res.workspace.id);
              navigate('/app/dashboard', { replace: true });
            } catch (err) {
              if (err instanceof ApiError) {
                const c = err.code;
                const friendly = c ? prettyKnownError(c) : null;
                setError(friendly ?? formatUnexpectedError(err));
              } else {
                setError(formatUnexpectedError(err));
              }
            }
          }}
        >
          <div>
            <label className="label" htmlFor="code">Invite code</label>
            <input
              id="code"
              type="text"
              required
              maxLength={32}
              autoFocus
              className="input mt-1 font-mono uppercase tracking-wider"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="MEETUP24"
            />
          </div>
          <div>
            <label className="label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              required
              maxLength={254}
              className="input mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="name">Your name</label>
            <input
              id="name"
              type="text"
              required
              maxLength={100}
              className="input mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="ws">Workspace name</label>
            <input
              id="ws"
              type="text"
              required
              maxLength={80}
              placeholder="e.g. Acme Marketing"
              className="input mt-1"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500">
              Where your short links live. You can rename it later.
            </p>
          </div>
          <TurnstileGate onToken={setTurnstileToken} onError={setError} />
          {error && (
            <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p>
          )}
          <button
            type="submit"
            disabled={redeem.isPending || blocked}
            className="btn-primary w-full"
            title={blocked ? 'Waiting for bot check…' : undefined}
          >
            {redeem.isPending ? 'Creating account…' : 'Create account'}
          </button>
          <p className="text-center text-xs text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-600 hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function prettyKnownError(code: string): string | null {
  switch (code) {
    case 'code_not_found':
      return "That invite code doesn't exist or has been disabled.";
    case 'code_expired':
      return 'That invite code has expired.';
    case 'code_exhausted':
      return "That invite code's already been used the maximum number of times.";
    case 'account_already_exists':
      return 'An account with that email already exists. Try signing in instead.';
    case 'invalid_code_format':
      return 'That code format looks off — only letters, digits, and hyphens.';
    case 'turnstile_failed':
      return 'Bot check failed. Refresh and try again.';
    default:
      return null;
  }
}
